import { Reference, useApolloClient } from "@apollo/client";
import { Modal } from "@msys/ui";
import { MenuItem, TextField, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, FormikProps } from "formik";
import { uniqueId } from "lodash-es";
import moment, { Moment } from "moment";
import { useSnackbar } from "notistack";
import { useMemo } from "react";
import * as Yup from "yup";
import { DatePickerField } from "../../commons/form-fields/DatePickerField.js";
import { AnnotationSeverity, Maybe } from "../../../clients/graphqlTypes.js";
import { useAddQuoteAnnotationMutation } from "./QuoteAnnotations.generated.js";

const TITLE_MAX = 100;
const BODY_MAX = 200;

const SEVERITIES: AnnotationSeverity[] = [
  "INFO",
  "WARNING",
  "SUCCESS",
  "ERROR",
];

const SEVERITY_LABEL: Record<
  AnnotationSeverity,
  "Info" | "Warning" | "Success" | "Error"
> = {
  INFO: "Info",
  WARNING: "Warning",
  SUCCESS: "Success",
  ERROR: "Error",
};

interface FormValues {
  title: string;
  body: string;
  severity: AnnotationSeverity;
  validFrom: Moment;
  validUntil: Maybe<Moment>;
}

function trimOrNull(s: string): string | null {
  const t = s.trim();
  return t === "" ? null : t;
}

interface Props {
  projectId: string;
  quoteId: string;
  handleClose: () => void;
}

export const AddQuoteAnnotationModal = ({
  projectId,
  quoteId,
  handleClose,
}: Props) => {
  const { t } = useTranslate(["Quote", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const formId = useMemo(() => uniqueId(), []);

  const [addQuoteAnnotation] = useAddQuoteAnnotationMutation({
    client,
  });

  const initialValues: FormValues = useMemo(
    () => ({
      title: "",
      body: "",
      severity: "INFO",
      validFrom: moment(),
      validUntil: null,
    }),
    []
  );

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .label(t("Title", { ns: "Quote" }))
      .trim()
      .max(TITLE_MAX),
    body: Yup.string()
      .label(t("Message", { ns: "Quote" }))
      .trim()
      .required()
      .max(BODY_MAX),
    severity: Yup.string()
      .oneOf(SEVERITIES)
      .label(t("Severity", { ns: "Quote" }))
      .required(),
    validFrom: Yup.date()
      .label(t("Valid from", { ns: "Quote" }))
      .required(),
    validUntil: Yup.date()
      .nullable()
      .label(t("Valid until", { ns: "Quote" }))
      .test(
        "laterThan",
        t("Valid until must be after Valid from", { ns: "Quote" }),
        function (validUntil) {
          if (!validUntil) return true;
          const validFrom: Date = this.resolve(Yup.ref("validFrom"));
          return validFrom < validUntil;
        }
      ),
  });

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={async values => {
        try {
          await addQuoteAnnotation({
            variables: {
              input: {
                projectId,
                quoteId,
                title: trimOrNull(values.title),
                body: values.body.trim(),
                severity: values.severity,
                // Only send an explicit validFrom when the user changed the
                // prefilled "now"; otherwise send null so Postgres owns the
                // timestamp via `DEFAULT now()` and client↔DB clock skew can't
                // push valid_from into the future.
                validFrom:
                  values.validFrom === initialValues.validFrom
                    ? null
                    : values.validFrom.toDate(),
                validUntil: values.validUntil
                  ? values.validUntil.toDate()
                  : null,
              },
            },
            update: (cache, { data }) => {
              if (!data) return;
              cache.modify<{ annotations: Reference[] }>({
                id: cache.identify({ __typename: "Quote", id: quoteId }),
                fields: {
                  annotations(existing = [], { toReference }) {
                    const newRef = toReference(data.addQuoteAnnotation);
                    return newRef ? [newRef, ...existing] : existing;
                  },
                },
              });
            },
          });
          handleClose();
        } catch (e) {
          if (e instanceof Error) {
            enqueueSnackbar(e.message, { variant: "error" });
          }
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        isValid,
        isSubmitting,
      }: FormikProps<FormValues>) => (
        <Modal
          title={t("Add note", { ns: "Quote" })}
          dialogProps={{
            maxWidth: "xs",
            fullWidth: true,
          }}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: { variant: "text", disabled: isSubmitting },
            },
            {
              label: t("Add", { ns: "Global" }),
              buttonProps: {
                type: "submit",
                form: formId,
                disabled: !isValid,
                loading: isSubmitting,
              },
            },
          ]}
          handleClose={handleClose}
        >
          <Form id={formId}>
            <Stack spacing={1}>
              <TextField
                name="title"
                label={t("Title (optional)", { ns: "Quote" })}
                value={values.title}
                onChange={handleChange}
                error={touched.title && Boolean(errors.title)}
                helperText={
                  (touched.title && errors.title) ||
                  `${values.title.length}/${TITLE_MAX}`
                }
                slotProps={{ htmlInput: { maxLength: TITLE_MAX } }}
                autoComplete="off"
                fullWidth
              />
              <TextField
                name="body"
                label={t("Message", { ns: "Quote" })}
                value={values.body}
                onChange={handleChange}
                error={touched.body && Boolean(errors.body)}
                helperText={
                  (touched.body && errors.body) ||
                  `${values.body.length}/${BODY_MAX}`
                }
                slotProps={{ htmlInput: { maxLength: BODY_MAX } }}
                autoComplete="off"
                multiline
                minRows={3}
                required
                fullWidth
              />
              <TextField
                name="severity"
                label={t("Severity", { ns: "Quote" })}
                value={values.severity}
                onChange={handleChange}
                select
                required
                fullWidth
              >
                {SEVERITIES.map(s => (
                  <MenuItem key={s} value={s}>
                    {t(SEVERITY_LABEL[s], { ns: "Global" })}
                  </MenuItem>
                ))}
              </TextField>
              <DatePickerField
                name="validFrom"
                label={t("Valid from", { ns: "Quote" })}
                required
              />
              <DatePickerField
                name="validUntil"
                minValue={values.validFrom}
                label={t("Valid until (optional)", { ns: "Quote" })}
              />
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
